import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { createPortal } from 'react-dom';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';

import {
  activeAdminTenantId,
  canWriteTenant,
  useAuth,
} from '../../auth/AuthContext';
import * as masterBaselinesApi from '../../api/masterBaselines';
import * as proceduresApi from '../../api/procedures';
import { extractErrorMessage } from '../../api/errors';
import { RefreshButton } from '../../components/RefreshButton';
import { SortHeader } from '../../components/SortHeader';
import { SearchableSelect } from '../../components/SearchableSelect';
import { sortRows, useTableSort } from '../../util/tableSort';
import { procedureColor } from '../../util/procedureColors';

const NOT_MAPPED = [{ value: '', label: 'Not mapped' }];

/**
 * Tenant-admin procedures catalog.
 *
 * One card per tenant: each procedure's own fields (name / session /
 * threshold / day) AND its baseline mapping live in a single sortable
 * table, both editable from the one pencil dialog. The capture-app pulls
 * this list per tenant when a session starts (ROADMAP #7).
 *
 * Numbering is server-assigned: max(procedure_number) + 1 within the
 * tenant. `day` is a bare date via a native HTML5 date input.
 *
 * Baseline mapping: maps a procedure to a
 * `public.master_baselines.procedure_group` so the dashboard's Faculty /
 * practice-ready comparison fires. Effective mapping = explicit row >
 * legacy `procedure_group` fallback > none.
 */
export function TenantProcedures({
  embedded = false,
  actionsSlot = null,
}: { embedded?: boolean; actionsSlot?: HTMLElement | null } = {}) {
  const { user } = useAuth();
  const tenantId = activeAdminTenantId(user);
  const canWrite = canWriteTenant(user, tenantId);

  const [procedures, setProcedures] = useState<proceduresApi.Procedure[] | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);

  // Form dialog state. `editing` is the procedure being edited, or null
  // when creating.
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] =
    useState<proceduresApi.Procedure | null>(null);
  const [formName, setFormName] = useState('');
  const [formThreshold, setFormThreshold] = useState<string>('70');
  const [formSession, setFormSession] = useState('');
  const [formDay, setFormDay] = useState<string>(''); // YYYY-MM-DD or empty
  const [formOperator, setFormOperator] = useState<string>('manual');
  // Baseline picker in the same dialog. `formBaselineInitial` is what it
  // opened with, so we only PUT a mapping change when the operator
  // actually touched it (avoids converting a legacy fallback to explicit
  // on an unrelated edit).
  const [formBaseline, setFormBaseline] = useState('');
  const [formBaselineInitial, setFormBaselineInitial] = useState('');
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Baseline data: `catalogue` = the baseline procedures to map TO;
  // `mapRows` = each procedure's effective mapping (explicit>legacy>none).
  const [catalogue, setCatalogue] = useState<
    masterBaselinesApi.MasterBaselineProcedure[]
  >([]);
  const [mapRows, setMapRows] = useState<
    masterBaselinesApi.BaselineMapEntry[] | null
  >(null);

  // Delete confirm.
  const [deleting, setDeleting] =
    useState<proceduresApi.Procedure | null>(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const loadMap = async () => {
    if (!tenantId) return;
    try {
      setMapRows(await masterBaselinesApi.getBaselineMap(tenantId));
    } catch {
      setMapRows([]);
    }
  };

  const reload = async () => {
    if (!tenantId) return;
    setError(null);
    setRefreshing(true);
    try {
      const rows = await proceduresApi.listProcedures(tenantId);
      setProcedures(rows);
      await loadMap();
    } catch (err: unknown) {
      setError(extractErrorMessage(err, 'failed to load procedures'));
      setProcedures([]);
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  // The baseline-procedure catalogue (mapping targets), once at mount.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const rows = await masterBaselinesApi.listMasterBaselineProcedures();
        if (!cancelled) setCatalogue(rows);
      } catch {
        // non-blocking — picker just stays empty
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // procedure_id → its effective baseline mapping entry.
  const mapByProc = useMemo(
    () => new Map((mapRows ?? []).map((m) => [m.procedure_id, m])),
    [mapRows],
  );

  // Effective baseline display for a procedure (catalogue name if the key
  // is known, else the raw legacy value, else null when unmapped).
  const baselineDisplay = (p: proceduresApi.Procedure): string | null => {
    const m = mapByProc.get(p.id);
    if (!m || !m.baseline_group) return null;
    const c = catalogue.find((x) => x.key === m.baseline_group);
    return c ? c.display_name : m.baseline_group;
  };

  const sort = useTableSort<
    | 'number'
    | 'name'
    | 'session'
    | 'threshold'
    | 'day'
    | 'operator'
    | 'baseline'
  >('number');
  const sortedProcedures = useMemo(() => {
    if (procedures === null) return null;
    return sortRows(
      procedures,
      (p) => {
        switch (sort.orderBy) {
          case 'number':
            return p.procedure_number;
          case 'name':
            return p.name;
          case 'session':
            return p.session_name;
          case 'threshold':
            return p.threshold_percent;
          case 'day':
            return p.day;
          case 'operator':
            return p.operator;
          case 'baseline':
            return baselineDisplay(p);
        }
      },
      sort.order,
    );
    // baselineDisplay closes over mapByProc/catalogue, tracked below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [procedures, sort.orderBy, sort.order, mapByProc, catalogue]);

  // The Operator column only earns its space once it varies — every tenant
  // starts all-'manual'. Hide it until at least one procedure is robotic
  // (it's still set/edited in the dialog, which brings the column back).
  const showOperator = (procedures ?? []).some((p) => p.operator === 'robotic');

  // The dialog's baseline picker prefill: the explicit/legacy group when
  // it's a known catalogue key, else blank (an unknown legacy value isn't
  // selectable, so we surface it as a caption instead).
  const baselineInitialFor = (p: proceduresApi.Procedure | null): string => {
    if (!p) return '';
    const m = mapByProc.get(p.id);
    if (m && catalogue.some((c) => c.key === m.baseline_group)) {
      return m.baseline_group ?? '';
    }
    return '';
  };

  const openCreate = () => {
    setEditing(null);
    setFormName('');
    setFormThreshold('70');
    setFormSession('');
    setFormDay('');
    setFormOperator('manual');
    setFormBaseline('');
    setFormBaselineInitial('');
    setFormError(null);
    setDialogOpen(true);
  };

  const openEdit = (p: proceduresApi.Procedure) => {
    setEditing(p);
    setFormName(p.name);
    setFormThreshold(String(p.threshold_percent));
    setFormSession(p.session_name ?? '');
    setFormDay(p.day ?? '');
    setFormOperator(p.operator || 'manual');
    const b = baselineInitialFor(p);
    setFormBaseline(b);
    setFormBaselineInitial(b);
    setFormError(null);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    if (formSubmitting) return;
    setDialogOpen(false);
  };

  const submitForm = async (e: FormEvent) => {
    e.preventDefault();
    if (!tenantId) return;

    const thresholdNum = Number(formThreshold);
    if (
      !Number.isInteger(thresholdNum) ||
      thresholdNum < 0 ||
      thresholdNum > 100
    ) {
      setFormError('Threshold must be an integer between 0 and 100.');
      return;
    }
    if (formName.trim() === '') {
      setFormError('Name is required.');
      return;
    }

    const sessionTrim = formSession.trim();
    const sessionPayload = sessionTrim === '' ? null : sessionTrim;

    setFormSubmitting(true);
    setFormError(null);
    try {
      let procedureId: string;
      if (editing) {
        await proceduresApi.updateProcedure(tenantId, editing.id, {
          name: formName.trim(),
          threshold_percent: thresholdNum,
          session_name: sessionPayload,
          day: formDay === '' ? null : formDay,
          operator: formOperator,
        });
        procedureId = editing.id;
      } else {
        const created = await proceduresApi.createProcedure(tenantId, {
          name: formName.trim(),
          threshold_percent: thresholdNum,
          session_name: sessionPayload,
          day: formDay === '' ? null : formDay,
          operator: formOperator,
        });
        procedureId = created.id;
      }
      // Only touch the mapping if the picker changed (or a create picked
      // one). Empty string clears the explicit mapping (falls back to
      // legacy / none).
      if (formBaseline !== formBaselineInitial) {
        await masterBaselinesApi.setBaselineMap(
          tenantId,
          procedureId,
          formBaseline || null,
        );
      }
      setDialogOpen(false);
      await reload();
    } catch (err: unknown) {
      setFormError(extractErrorMessage(err, 'failed to save procedure'));
    } finally {
      setFormSubmitting(false);
    }
  };

  const confirmDelete = async () => {
    if (!tenantId || !deleting) return;
    setDeleteSubmitting(true);
    setError(null);
    try {
      await proceduresApi.deleteProcedure(tenantId, deleting.id);
      setDeleting(null);
      await reload();
    } catch (err: unknown) {
      setError(extractErrorMessage(err, 'failed to delete procedure'));
    } finally {
      setDeleteSubmitting(false);
    }
  };

  if (!tenantId) {
    return (
      <Alert severity="warning">
        You don't currently hold an active tenant_admin role. If this is
        unexpected, sign out and back in to refresh your session.
      </Alert>
    );
  }

  // Baseline mapping is one of the most useful things in the catalog, so lean
  // into it: a clear status (✓ Mapped / ⚠ Legacy / ○ Needs mapping) with its
  // icon + colour, and the mapped baseline name beneath rather than tucked to
  // the side.
  const baselineChip = (p: proceduresApi.Procedure) => {
    const m = mapByProc.get(p.id);
    const source = m?.source ?? 'none';
    const meta =
      source === 'explicit'
        ? {
            label: 'Mapped',
            color: 'success.main',
            icon: <CheckCircleIcon sx={{ fontSize: 18 }} />,
          }
        : source === 'legacy'
          ? {
              label: 'Legacy',
              color: 'warning.main',
              icon: <WarningAmberIcon sx={{ fontSize: 18 }} />,
            }
          : {
              label: 'Needs mapping',
              color: 'text.disabled',
              icon: <RadioButtonUncheckedIcon sx={{ fontSize: 18 }} />,
            };
    const display = baselineDisplay(p);
    return (
      <Box>
        <Stack
          direction="row"
          spacing={0.75}
          alignItems="center"
          sx={{ color: meta.color }}
        >
          {meta.icon}
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {meta.label}
          </Typography>
        </Stack>
        {display && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', pl: '25px', lineHeight: 1.3 }}
          >
            {display}
          </Typography>
        )}
      </Box>
    );
  };

  const actions = (
    <Stack direction="row" spacing={1}>
      <RefreshButton onClick={() => void reload()} refreshing={refreshing} />
      <Button
        variant="contained"
        startIcon={<AddIcon />}
        onClick={openCreate}
        disabled={!canWrite}
      >
        New procedure
      </Button>
    </Stack>
  );

  return (
    <Stack spacing={3}>
      {/* When hosted in the combined Procedures page, the actions render into
          the tab bar's slot (same line as the tabs) instead of a own row. */}
      {actionsSlot ? (
        createPortal(actions, actionsSlot)
      ) : (
        <Stack
          direction="row"
          justifyContent={embedded ? 'flex-end' : 'space-between'}
          alignItems="center"
        >
          {!embedded && (
            <Stack>
              <Typography variant="h4">Procedures</Typography>
              <Typography variant="body2" color="text.secondary">
                The catalog of procedures evaluated in your tenant, each with
                its baseline mapping. The capture-app downloads this list when
                a session starts.
              </Typography>
            </Stack>
          )}
          {actions}
        </Stack>
      )}

      {!canWrite && (
        <Alert severity="info">
          Your admin account is <strong>read-only</strong>. You can view
          procedures but can't create, edit or delete them.
        </Alert>
      )}

      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {procedures === null ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : procedures.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            No procedures yet. Click <strong>New procedure</strong> to add
            your first one.
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <SortHeader
                  label="ID"
                  col="number"
                  sort={sort}
                  sx={{ width: 80 }}
                />
                <SortHeader label="Name" col="name" sort={sort} />
                <SortHeader label="Session" col="session" sort={sort} />
                <SortHeader
                  label="Threshold"
                  col="threshold"
                  sort={sort}
                  sx={{ width: 110 }}
                />
                <SortHeader
                  label="Day"
                  col="day"
                  sort={sort}
                  sx={{ width: 140 }}
                />
                {showOperator && (
                  <SortHeader
                    label="Operator"
                    col="operator"
                    sort={sort}
                    sx={{ width: 110 }}
                  />
                )}
                <SortHeader label="Baseline" col="baseline" sort={sort} />
                <TableCell align="right" sx={{ width: 120 }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(sortedProcedures ?? []).map((p) => (
                <TableRow key={p.id} hover>
                  <TableCell sx={{ fontFamily: 'monospace' }}>
                    #{p.procedure_number}
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1.25} alignItems="center">
                      {/* Consistent leading dot per procedure — the same
                          deterministic colour used on the Activity page, so a
                          procedure reads by colour across the portal. */}
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          flexShrink: 0,
                          bgcolor: procedureColor(p.name),
                        }}
                      />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {p.name}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    {p.session_name ?? <span style={{ opacity: 0.5 }}>—</span>}
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Box
                        sx={{
                          width: 60,
                          height: 6,
                          borderRadius: 3,
                          bgcolor: 'action.hover',
                          overflow: 'hidden',
                          flexShrink: 0,
                        }}
                      >
                        <Box
                          sx={{
                            width: `${p.threshold_percent}%`,
                            height: '100%',
                            bgcolor: 'primary.main',
                          }}
                        />
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 700, minWidth: 34 }}
                      >
                        {p.threshold_percent}%
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>{p.day ?? '—'}</TableCell>
                  {showOperator && (
                    <TableCell>
                      <Chip
                        size="small"
                        label={p.operator === 'robotic' ? 'Robotic' : 'Manual'}
                        color={p.operator === 'robotic' ? 'info' : 'default'}
                        variant={p.operator === 'robotic' ? 'filled' : 'outlined'}
                      />
                    </TableCell>
                  )}
                  <TableCell>{baselineChip(p)}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit procedure">
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => openEdit(p)}
                          aria-label={`Edit procedure ${p.procedure_number}`}
                          disabled={!canWrite}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Delete procedure">
                      <span>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => setDeleting(p)}
                          aria-label={`Delete procedure ${p.procedure_number}`}
                          disabled={!canWrite}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="sm">
        {/* noValidate: keep the HTML5 hints (spinner/min/max) but let the
            JS validator in submitForm own submission. */}
        <form onSubmit={submitForm} noValidate>
          <DialogTitle>
            {editing
              ? `Edit procedure #${editing.procedure_number}`
              : 'New procedure'}
          </DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              The procedure ID is assigned automatically when you save.
            </DialogContentText>
            <Stack spacing={2}>
              <TextField
                autoFocus
                label="Procedure name"
                fullWidth
                required
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                disabled={formSubmitting}
                inputProps={{ maxLength: 120 }}
              />
              <TextField
                label="Session name (optional)"
                fullWidth
                value={formSession}
                onChange={(e) => setFormSession(e.target.value)}
                disabled={formSubmitting}
                inputProps={{ maxLength: 120 }}
                helperText="Group label for procedures in the same exercise block. Leave blank for one-offs."
              />
              <TextField
                label="Threshold"
                type="number"
                fullWidth
                required
                value={formThreshold}
                onChange={(e) => setFormThreshold(e.target.value)}
                disabled={formSubmitting}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">%</InputAdornment>
                  ),
                }}
                inputProps={{ min: 0, max: 100, step: 1 }}
                helperText="Integer 0–100."
              />
              <TextField
                label="Day (optional)"
                type="date"
                fullWidth
                value={formDay}
                onChange={(e) => setFormDay(e.target.value)}
                disabled={formSubmitting}
                InputLabelProps={{ shrink: true }}
                helperText="Pick a calendar date or leave blank."
              />
              <SearchableSelect
                label="Operator"
                fullWidth
                size="medium"
                value={formOperator}
                onChange={setFormOperator}
                disabled={formSubmitting}
                helperText="How the procedure is performed. Sessions inherit this, and the scorecard only compares against same-operator baselines."
                options={proceduresApi.PROCEDURE_OPERATORS}
              />
              <SearchableSelect
                label="Baseline procedure"
                fullWidth
                size="medium"
                value={formBaseline}
                onChange={setFormBaseline}
                disabled={formSubmitting}
                helperText="Maps to the faculty / practice-ready reference on the dashboard. Leave unmapped to show no faculty bars."
                pinned={NOT_MAPPED}
                options={catalogue.map((c) => ({
                  value: c.key,
                  label: c.display_name,
                }))}
              />
              {editing &&
                mapByProc.get(editing.id)?.source === 'legacy' &&
                formBaselineInitial === '' && (
                  <Typography variant="caption" color="text.secondary">
                    Current legacy value:{' '}
                    {mapByProc.get(editing.id)?.baseline_group} — pick from
                    the list to make it explicit.
                  </Typography>
                )}
              {formError && <Alert severity="error">{formError}</Alert>}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeDialog} disabled={formSubmitting}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={formSubmitting}>
              {formSubmitting ? 'Saving…' : editing ? 'Save' : 'Create'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog
        open={deleting !== null}
        onClose={() => (deleteSubmitting ? null : setDeleting(null))}
      >
        <DialogTitle>
          Delete {deleting && `#${deleting.procedure_number} — ${deleting.name}`}
          ?
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            This soft-deletes the procedure. Sessions that referenced it
            stay coherent — only the live catalog drops the row.
            Re-creating it later gets a new auto-assigned number.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleting(null)} disabled={deleteSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={() => void confirmDelete()}
            color="error"
            variant="contained"
            disabled={deleteSubmitting}
          >
            {deleteSubmitting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
